import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { useGetApiCompaniesCurrentEmployeesReportingAveragepointsYearMonth } from '@/api/generated/company-management-user/company-management-user'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Rectangle, Legend } from 'recharts'
import { CHART_COLORS, TAKE_OPTIONS, YEAR_OPTIONS, getMonthLabels, CURRENT_YEAR, CURRENT_MONTH } from './constants'
import { ChartCard } from '../components/ChartCard'
import { ChartTooltip } from '../components/ChartTooltip'
import { FilterRowCharts } from '@/components/common/filters/FilterRowCharts'
import { FilterRowChartItem } from '@/components/common/filters/FilterRowChartItem'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

export function AveragePointsChart() {
  const { t } = useTranslation(['dashboard', 'common'])
  const [year, setYear] = useState(CURRENT_YEAR)
  const [month, setMonth] = useState(CURRENT_MONTH)
  const [count, setCount] = useState(5)
  const monthLabels = getMonthLabels(t)

  const { data, isFetching, isError } = useGetApiCompaniesCurrentEmployeesReportingAveragepointsYearMonth(
    year,
    month,
    { take: count }
  )

  const chartData = (data?.data ?? [])
    .map((item, index) => ({
      name: [item.firstName, item.lastName].filter(Boolean).join(' ') || '—',
      value: Math.round(item.averagePoints),
      fill: CHART_COLORS[index % CHART_COLORS.length],
    }))
    .sort((first, second) => second.value - first.value)

  function chart() {
    return (
      <LoaderGeneral isLoading={isFetching} isError={isError} isEmpty={chartData.length === 0} className="h-[260px]">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 48, left: 8 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--foreground)' }} tickLine={false} axisLine={false} angle={-35} textAnchor="end" interval={0} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} />
            <Tooltip content={<ChartTooltip valueLabel={t('charts.averagePoints.tooltipLabel')} />} cursor={{ fill: 'var(--muted)' }} />
            <Legend formatter={() => t('charts.averagePoints.legend')} />
            <Bar
              dataKey="value"
              radius={[4, 4, 0, 0]}
              maxBarSize={36}
              shape={(props: any) => <Rectangle {...props} fill={props.payload.fill} />}
            />
          </BarChart>
        </ResponsiveContainer>
      </LoaderGeneral>
    )
  }

  function controls() {
    return (
      <FilterRowCharts>
        <FilterRowChartItem label={t('charts.filters.year')}>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEAR_OPTIONS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterRowChartItem>
        <FilterRowChartItem label={t('charts.filters.month')}>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthLabels.map((label, i) => <SelectItem key={i + 1} value={String(i + 1)}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterRowChartItem>
        <FilterRowChartItem label={t('charts.filters.count')}>
          <Select value={String(count)} onValueChange={(v) => setCount(Number(v))}>
            <SelectTrigger className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TAKE_OPTIONS.map((number) => <SelectItem key={number} value={String(number)}>{number}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterRowChartItem>
      </FilterRowCharts>
    )
  }

  return (
    <ChartCard
      title={t('charts.averagePoints.title')}
      chart={chart()}
      controls={controls()}
    />
  )
}
